/**
 * reloadInterceptor.ts
 *
 * Uses the Navigation API to intercept F5/Ctrl+R/refresh-button presses
 * and convert them into same-document navigations, keeping the SmolLM3
 * model alive in memory.
 *
 * Safety valves:
 * 1. Version check — if a new deployment is detected, allow a real reload
 * 2. Soft reload counter — after MAX_SOFT_RELOADS, force a real reload
 * 3. Memory pressure check — if JS heap exceeds threshold, force a real reload
 * 4. Worker health check — if the model worker is unresponsive, force a real reload
 * 5. Force-reload flag — allows programmatic "real" reloads via sessionStorage
 */

// Build hash injected by Vite at build time (see vite.config.ts)
// Falls back to empty string in dev mode
const APP_VERSION: string = process.env.REACT_APP_BUILD_HASH || '';

// ============================================================================
// Configuration
// ============================================================================

const MAX_SOFT_RELOADS = 25;
const MEMORY_THRESHOLD_MB = 3000; // Force real reload if JS heap exceeds 3 GB
const VERSION_CHECK_URL = '/version.json'; // Static file generated at build time
const WORKER_PING_TIMEOUT_MS = 2000; // How long to wait for worker response

// ============================================================================
// State
// ============================================================================

let softReloadCount = 0;
let isInitialized = false;

// ============================================================================
// Version Check
// ============================================================================

async function isNewVersionDeployed(): Promise<boolean> {
  // Skip version check in development (no build hash)
  if (!APP_VERSION) return false;

  try {
    const res = await fetch(VERSION_CHECK_URL, {
      cache: 'no-store',
      signal: AbortSignal.timeout(3000),
    });
    if (!res.ok) return false;
    const data = await res.json();
    return data.version !== APP_VERSION;
  } catch {
    // Network error or timeout — don't block the soft reload
    console.warn('[ReloadInterceptor] Version check failed, proceeding with soft reload');
    return false;
  }
}

// ============================================================================
// Memory Pressure Check
// ============================================================================

async function isMemoryPressureHigh(): Promise<boolean> {
  // performance.measureUserAgentSpecificMemory is Chrome-only and requires
  // cross-origin isolation. Fall back gracefully if unavailable.
  if ('measureUserAgentSpecificMemory' in performance) {
    try {
      const mem = await (performance as any).measureUserAgentSpecificMemory();
      const heapMB = mem.bytes / (1024 * 1024);
      if (heapMB > MEMORY_THRESHOLD_MB) {
        console.log(
          `[ReloadInterceptor] Memory pressure high: ${heapMB.toFixed(0)} MB > ${MEMORY_THRESHOLD_MB} MB`
        );
        return true;
      }
    } catch {
      // Not available — ignore
    }
  }
  return false;
}

// ============================================================================
// Worker Health Check
// ============================================================================

/**
 * Pings the Qwen3 worker to check if it's responsive.
 * Returns true if the worker responds within the timeout, false otherwise.
 */
async function isWorkerHealthy(): Promise<boolean> {
  try {
    // Import Qwen3Manager dynamically to avoid circular dependencies
    const { default: Qwen3Manager } = await import('./Qwen3Manager');
    const manager = Qwen3Manager.getInstance();

    // If the model isn't ready, there's nothing to preserve — soft reload is fine
    if (!manager.isReady()) return true;

    // Ping the worker
    return await manager.pingWorker(WORKER_PING_TIMEOUT_MS);
  } catch {
    // If we can't even import the manager, something is wrong
    return false;
  }
}

// ============================================================================
// Soft Reload Handler
// ============================================================================

function performSoftReload(): void {
  // 1. Brief visual feedback (fade out and back in)
  document.body.style.transition = 'opacity 0.15s ease-out';
  document.body.style.opacity = '0.3';

  requestAnimationFrame(() => {
    setTimeout(() => {
      // 2. Dispatch custom event for React to handle
      //    React components listen for this to reset their state
      window.dispatchEvent(new CustomEvent('soft-reload'));

      // 3. Scroll to top (mimics real refresh behavior)
      window.scrollTo(0, 0);

      // 4. Fade back in
      document.body.style.transition = 'opacity 0.3s ease-in';
      document.body.style.opacity = '1';

      // 5. Clean up transition style after animation completes
      setTimeout(() => {
        document.body.style.transition = '';
      }, 350);
    }, 150);
  });
}

// ============================================================================
// Force Real Reload (for programmatic use)
// ============================================================================

/**
 * Call this when you need a REAL page reload (e.g., after detecting
 * a critical error that can only be fixed by restarting).
 * Sets a sessionStorage flag so the interceptor lets the reload through.
 */
export function forceRealReload(): void {
  sessionStorage.setItem('force-real-reload', 'true');
  window.location.reload();
}

// ============================================================================
// Main Initialization
// ============================================================================

export function initReloadInterceptor(): void {
  // Guard: only initialize once (survives across soft reloads since
  // this module's top-level state persists)
  if (isInitialized) {
    console.log('[ReloadInterceptor] Already initialized, skipping');
    return;
  }

  // Feature detection: check if Navigation API is available
  const nav = (window as any).navigation;
  if (!nav) {
    console.warn(
      '[ReloadInterceptor] Navigation API not available in this browser. ' +
      'Model will reload from network on every refresh.'
    );
    return;
  }

  console.log('[ReloadInterceptor] Initializing Navigation API reload interceptor');

  nav.addEventListener('navigate', (event: any) => {
    // Only intercept reload navigations (F5, Ctrl+R, refresh button)
    if (event.navigationType !== 'reload') return;

    // Only intercept if the browser allows it (same-origin, same URL)
    if (!event.canIntercept) return;

    // Check for programmatic force-reload flag
    if (sessionStorage.getItem('force-real-reload')) {
      sessionStorage.removeItem('force-real-reload');
      console.log('[ReloadInterceptor] Force-reload flag detected — allowing real refresh');
      return; // Don't intercept
    }

    // Increment soft reload counter
    softReloadCount++;

    // Safety valve: after MAX_SOFT_RELOADS, force a real refresh
    if (softReloadCount > MAX_SOFT_RELOADS) {
      console.log(
        `[ReloadInterceptor] Soft reload limit reached (${softReloadCount}) — allowing real refresh`
      );
      softReloadCount = 0;
      return; // Don't intercept
    }

    // Intercept the reload — this is where the magic happens
    event.intercept({
      handler: async () => {
        console.log(`[ReloadInterceptor] Soft reload #${softReloadCount}`);

        // Safety valve: check for new deployment
        const newVersion = await isNewVersionDeployed();
        if (newVersion) {
          console.log('[ReloadInterceptor] New version detected — forcing real refresh');
          softReloadCount = 0;
          forceRealReload();
          return;
        }

        // Safety valve: check memory pressure (every 5th soft reload)
        if (softReloadCount % 5 === 0) {
          const highMemory = await isMemoryPressureHigh();
          if (highMemory) {
            console.log('[ReloadInterceptor] Memory pressure high — forcing real refresh');
            softReloadCount = 0;
            forceRealReload();
            return;
          }
        }

        // Safety valve: check worker health (every 3rd soft reload)
        if (softReloadCount % 3 === 0) {
          const healthy = await isWorkerHealthy();
          if (!healthy) {
            console.log('[ReloadInterceptor] Worker unresponsive — forcing real refresh');
            softReloadCount = 0;
            forceRealReload();
            return;
          }
        }

        // All checks passed — perform the soft reload
        performSoftReload();
      },
    });
  });

  isInitialized = true;
  console.log('[ReloadInterceptor] ✅ Reload interceptor active');
}
